import { AfterViewInit, Component, OnInit } from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { TableDashboardItemHelper } from 'src/app/components/table-dashboard-item/table-dashboard-item-helper';
import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import { DatasourceColumnId } from 'src/app/utils/datasource-sql-map/datasource';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { ModifyTimeDashboardItemFactory } from '../common/items/modify-time';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { MopaSkadNetworkChartDocs } from './mopa-skadnetwork.chart-documentation';
import { SkadNetworkDataLoaderFactory } from './data-loaders/skan-postbacks';
import { SkanTableDashoardItemHelper } from './item/skan-table';
import { BiStgSkanDecodedPostbacks } from './sql-maps/bistg-skan-decoded-postbacks-datasource-sql-map';

@Component({
    selector: 'app-mopa-skadnetwork',
    templateUrl: './mopa-skadnetwork.component.html',
    styleUrls: ['./mopa-skadnetwork.component.scss'],
    providers: [DashboardService, QueryContextService],
})
export class MopaSkadNetworkComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Skad_Network';
    override DASHBOARD_ID = 'mopa-skadnetwork';

    values = Object.values;

    private defaultGcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'mopa.skadnetwork.filter.activity_date': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.skadnetwork.filter.activity_date',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_DATE',
            datasourceSqlMap: BiStgSkanDecodedPostbacks.sqlMap,
        }).create(),
        'mopa.skadnetwork.filter.attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.skadnetwork.filter.attribution_campaign_name',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                datasourceSqlMap: BiStgSkanDecodedPostbacks.sqlMap,
            }).create(),
        'mopa.skadnetwork.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.skadnetwork.filter.attribution_network_name',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
            datasourceSqlMap: BiStgSkanDecodedPostbacks.sqlMap,
        }).create(),
        'mopa.skadnetwork.filter.skad_postback_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.skadnetwork.filter.skad_postback_source',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SKAD_POSTBACK_SOURCE',
            datasourceSqlMap: BiStgSkanDecodedPostbacks.sqlMap,
        }).create(),
    };

    override chartDataLoaders = {
        'mopa.skadnetwork.modify_time_skadnetwork_datasource':
            DataLoaderFactories['common.modify_time']({
                createdInstanceId:
                    'mopa.skadnetwork.modify_time_skadnetwork_datasource',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasetId: 'visualization',
                tableId: 'skadnetwork_postbacks',
            }).create(),
        'mopa.skadnetwork.mastertable': new SkadNetworkDataLoaderFactory({
            createdInstanceId: 'mopa.skadnetwork.mastertable',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // CHARTS
    modify_time_skadnetwork_datasource: IDashboardItemDataLoaderLink = {
        id: 'mopa.skadnetwork.modify_time_skadnetwork_datasource',
        dashboardItemFactory: new ModifyTimeDashboardItemFactory({
            createdInstanceId:
                'mopa.skadnetwork.modify_time_skadnetwork_datasource',
            columnId: 'value',
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.skadnetwork.modify_time_skadnetwork_datasource'
            ].emitter,
    };

    mastertable = {
        id: 'mopa.skadnetwork.mastertable',
        dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
            dashboardName: 'Skad_Network',
            instanceId: 'mopa.skadnetwork.mastertable',
            dashboardItem: {
                title: 'SkadNetwork mastertable',
                chartDocumentation: MopaSkadNetworkChartDocs.mastertable,
                viewMode: 'scroll',
            },
            columnsGetter:
                SkanTableDashoardItemHelper.tableSkanPostbacksColumnsGetter,
            splitColumnsSortFn:
                SkanTableDashoardItemHelper.tableSkanPostbacksSortSplitColumnsFn,
            splitTableByQueryContext: true,
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.skadnetwork.mastertable'].emitter,
    };

    // FILTERS
    settingSelectedMetrics: IMultiSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.SKADNETWORK_SELECTED_METRICS,
            viewtype: 'multiSelect',
            displayName: 'Selected metrics',
            value: this.getInitialContext().settings[
                QueryContextSettingID.SKADNETWORK_SELECTED_METRICS
            ],
            selectOptions:
                SkanTableDashoardItemHelper.tableSkanPostbacksColumnsGetter().map(
                    (metric: TableColumnItem) => {
                        return { value: metric.id, label: metric.name };
                    }
                ),
        },
    } as IMultiSelectSettingDashboardItem;

    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'mopa.skadnetwork.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'mopa.skadnetwork.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                forcedSplitable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.skadnetwork.filter.activity_date']
                    .emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.skadnetwork.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.skadnetwork.filter.attribution_campaign_name',
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                splitable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.skadnetwork.filter.attribution_campaign_name'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.skadnetwork.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.skadnetwork.filter.attribution_network_name',
                datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
                splitable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.skadnetwork.filter.attribution_network_name'
                ].emitter,
        },
        SKAD_POSTBACK_SOURCE: {
            id: 'mopa.skadnetwork.filter.skad_postback_source',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.skadnetwork.filter.skad_postback_source',
                datasourceColumnId: 'SKAD_POSTBACK_SOURCE',
                splitable: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.skadnetwork.filter.skad_postback_source'
                ].emitter,
        },
    };

    override ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaSkadNetworkComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }
    ngAfterViewInit() {
        console.log({
            msg: 'MopaSkadNetworkComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const campaignNameIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'ATTRIBUTION_CAMPAIGN_NAME',
                state: {
                    value: {
                        options: [],
                        filterType: 'include',
                    },
                    isFilterApplied: false,
                    isSplitApplied: true,
                },
            };

        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 30 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: true,
                },
            };

        const networkNameIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'ATTRIBUTION_NETWORK_NAME',
                state: {
                    value: {
                        options: [],
                        filterType: 'include',
                    },
                    isFilterApplied: false,
                    isSplitApplied: true,
                },
            };

        const postbackSourceIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'SKAD_POSTBACK_SOURCE',
                state: {
                    value: {
                        options: [],
                        filterType: 'include',
                    },
                    isFilterApplied: false,
                    isSplitApplied: false,
                },
            };

        initialContext.datasourceColumns = [
            campaignNameIC,
            activityDateIC,
            networkNameIC,
            postbackSourceIC,
        ];

        initialContext.settings[
            QueryContextSettingID.SKADNETWORK_SELECTED_METRICS
        ] =
            QueryContextDefaultSettings[
                QueryContextSettingID.SKADNETWORK_SELECTED_METRICS
            ]!;

        return initialContext;
    }
}
